import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { renderAppFabricWidget } from 'components/Shared/AppFabric/appfabricDevelopmentKit';
import Spinner from 'components/Shared/Spinner/Spinner';
import { useSelector } from 'store/utils';

enum InitialAuthenticationAttemptOutcome {
  SIGN_IN_SUCCESS = 'SIGN_IN_SUCCESS',
  SOMETHING_ELSE_REQUIRED = 'SOMETHING_ELSE_REQUIRED',
  NO_OPTIONS_FOUND = 'NO_OPTIONS_FOUND',
  ERROR = 'ERROR',
  // DIGITAL_IDENTITY_CREATED: 'DIGITAL_IDENTITY_CREATED',
}

enum SecurityCategory {
  Blue = 'Blue', // AAL=15
  Green = 'Green', // AAL=20
  Orange = 'Orange', // AAL=null (23 or 25, depending on identity provider)
  Red = 'Red', // AAL=25
  Grey = 'Grey', // AAL=30
  // BLUE = 'BLUE', // AAL=15
  // GREEN = 'GREEN', // AAL=20
  // ORANGE = 'ORANGE', // AAL=null (23 or 25, depending on identity provider)
  // RED = 'RED', // AAL=25
  // GREY = 'GREY', // AAL=30
}

/**
 * https://github.intuit.com/identity-authn/identity-authn-core-ui/blob/master/src/js/components/SignInSignUp/configuration/models/public/InitialAuthenticationAttemptCompleted.model.ts
 */
type InitialAuthenticationAttemptResult = {
  outcome: InitialAuthenticationAttemptOutcome;
  moreInfo: {
    securityCategoryAchieved?: SecurityCategory;
    digitalIdentity?: {
      legacyAuthId: string;
      username: string;
      /**
       * not yet available from SIO,
       * add this after Identity_BrowserSignInAuthenticationSessionBoundDigitalIdentity is updated:
       * digitalIdentityId: string;
       */
    };
    frictionlessCheckout?: {
      interactionRequired: boolean;
    };
    // Possible future addition: availableNextSteps: ARRAY<SioResponse['options']['optionType']>; // ie: "DIGITAL_IDENTITY_SELECTION" ->
  };
};

export type OnAccountCreatedSuccessResult = {
  id: string;
  legacyAuthId: string;
  pseudonymId: string;
  username: string;
  credentials: Array<{
    credentialType: 'EMAIL' | 'PHONE';
    emailCredential?: {
      emailAddress: string;
    };
    phoneCredential?: {
      phoneNumber: string;
    };
  }>;
};

export type FrictionlessReadyEvent = InitialAuthenticationAttemptResult | undefined;

export type OnCloseReason = 'ACCOUNT_COLLISION_CLOSE_BUTTON';

interface FrictionlessSignupProps {
  onComplete: () => void;
  onError: (error: Error) => void;
  onReady: (startedAt: Date, event?: FrictionlessReadyEvent) => void;
  onAccountCreatedSuccess: (event: OnAccountCreatedSuccessResult) => void;
  onOtpCancel: () => void;
  onClose: (reason: OnCloseReason) => void;
  onDuplicateUsername: () => void;
  email: string;
  phone: string;
}

type NestedObject = {
  [key: string]: string | boolean | number | NestedObject | ((...args: any[]) => void);
};

declare global {
  interface Window {
    AppFabric: {
      init: () => Promise<{
        renderWidget: (
          id: string,
          opts: {
            version: string;
            container: HTMLElement;
            props: NestedObject;
          }
        ) => Promise<void>;
      }>;
    };
  }
}

const FrictionlessSignup: React.FC<FrictionlessSignupProps> = ({
  email,
  phone,
  onComplete,
  onError,
  onReady,
  onOtpCancel,
  onClose,
  onDuplicateUsername,
  onAccountCreatedSuccess,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const APPFABRIC_DEVELPMENT_KIT_SRC = useSelector(
    (state) => state.config.endpoints.appfabricDevelopmentKitJsSdk
  );
  useEffect(() => {
    renderIdentity();
  }, []);

  const handleDuplicateUsername = () => {
    setError(true);
    onDuplicateUsername();
  };

  const renderIdentity = async () => {
    try {
      const identityWidgetLoadStart = new Date();
      const props = {
        email,
        phone,
        isRemediationOnly: false,
        onDone: async () => {
          onComplete();
        },
        onError: handleError,
        onReady: (event: FrictionlessReadyEvent) => {
          onReady(identityWidgetLoadStart, event);
          setIsLoading(false);
        },
        onAccountCreatedSuccess,
        onCancel: () => {
          onOtpCancel();
        },
        onClose,
        onDuplicateUsername: handleDuplicateUsername,
      };
      await renderAppFabricWidget(
        APPFABRIC_DEVELPMENT_KIT_SRC,
        'cpclient-webplugin/frictionless-login',
        containerRef.current as HTMLDivElement,
        props,
        () => {
          handleError(new Error('ADK failed to load'));
        }
      );
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleError = (error: Error) => {
    setError(true);
    setIsLoading(false);
    onError(error);
  };

  return (
    <>
      <div className={cn('container')}>
        <div className="loading-container">
          {isLoading && <Spinner />}
          {error && (
            <FormattedMessage
              id="PAYNOW_SOMETHING_WENT_WRONG"
              defaultMessage="Something went wrong"
            />
          )}
          <div ref={containerRef} />
        </div>
      </div>
      <style jsx>{`
        .container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .loading-container {
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
      `}</style>
    </>
  );
};

export default FrictionlessSignup;
